import { create } from 'zustand';

export type ToastType = 'success' | 'error' | 'info' | 'warning';

export interface Toast {
  id: string;
  type: ToastType;
  message: string;
  duration?: number;
}

interface ModalState {
  addSubscription: boolean;
  editSubscription: string | null;
  upgrade: boolean;
  deleteConfirm: string | null;
}

interface UIState {
  modals: ModalState;
  toasts: Toast[];
  sidebarOpen: boolean;

  // Modal actions
  openModal: <K extends keyof ModalState>(modal: K, data?: ModalState[K]) => void;
  closeModal: (modal: keyof ModalState) => void;
  closeAllModals: () => void;

  // Toast actions
  showToast: (type: ToastType, message: string, duration?: number) => void;
  removeToast: (id: string) => void;
  clearToasts: () => void;

  // Sidebar actions
  toggleSidebar: () => void;
  setSidebarOpen: (open: boolean) => void;
}

const defaultModals: ModalState = {
  addSubscription: false,
  editSubscription: null,
  upgrade: false,
  deleteConfirm: null,
};

export const useUIStore = create<UIState>((set) => ({
  modals: defaultModals,
  toasts: [],
  sidebarOpen: false,

  openModal: (modal, data) => set((state) => ({
    modals: {
      ...state.modals,
      [modal]: data !== undefined ? data : true
    }
  })),

  closeModal: (modal) => set((state) => ({
    modals: {
      ...state.modals,
      [modal]: modal === 'editSubscription' || modal === 'deleteConfirm' ? null : false
    }
  })),

  closeAllModals: () => set({ modals: defaultModals }),

  showToast: (type, message, duration = 5000) => set((state) => ({
    toasts: [
      ...state.toasts,
      {
        id: `toast-${Date.now()}-${Math.random()}`,
        type,
        message,
        duration,
      }
    ]
  })),

  removeToast: (id) => set((state) => ({
    toasts: state.toasts.filter(t => t.id !== id)
  })),

  clearToasts: () => set({ toasts: [] }),

  toggleSidebar: () => set((state) => ({ sidebarOpen: !state.sidebarOpen })),

  setSidebarOpen: (open) => set({ sidebarOpen: open }),
}));
